<script setup lang="ts">
  import NotifyMe from '@/components/notify.vue'
  import { ref, computed, onMounted as mounted, provide } from 'vue'
  import { useRouter as use_router } from 'vue-router'
  import { use_user, me } from '@/stores/user'
  import { use_participants } from '@/stores/participant'
  import { use_notify } from '@/stores/notify'
  import { callable } from '@/utils/serverless'
  import package_json from '@/../package.json'

  const router = use_router()
  const { signout, roles } = use_user()
  const { add_participant_to_project } = use_participants()
  const { log } = use_notify()

  const drawer = ref(false)
  const clipped = ref(false)
  const mini_variant = ref(false)

  const version = ref(package_json.version)
  const session = sessionStorage

  const title = ref('')
  provide('title', title)
  const initials = computed(() => {
    if (me.value && me.value.name)
      return me.value.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
    return ''
  })

  const sync_relations = async () => {
    log('sync_relations', 'starting')
    const start = performance.now()
    await callable('relations')()
    const duration = performance.now() - start
    log('sync_relations', 'complete', `${duration.toFixed(0)}ms`)
    session.relations = true
  }

  const view_me = () => {
    router.push(`/participants/${me.value.id}`)
  }

  const view_company = (company: Company) => {
    router.push(`/companies/${company.ref.id}`)
  }

  const view_terms = () => {
    window.location.href = 'https://sharemorestories.com/terms-of-use/'
  }

  const view_privacy = () => {
    window.location.href = 'https://sharemorestories.com/privacy-policy/'
  }
  const working = ref(false)
  mounted(async () => {
    log('app-load', me.value?.name, me.value?.id)
    if (me.value?.active.toLowerCase().trim() === 'new')
      router.push(`/participants/${me.value.id}/sign-up`)
    else if (session.project && me.value) {
      working.value = true
      const project_id = session.project
      log(`adding ${me?.value?.username} to project`, project_id)
      await add_participant_to_project(project_id, me?.value?.id)
      session.removeItem('project')
      await sync_relations()
      working.value = false
      router.push(`/participants/${me?.value?.id}/projects/${project_id}`)
    } else if (!session.relations && me.value) await sync_relations()
  })
</script>

<template>
  <v-app-bar color="primary-darken-3">
    <template #prepend>
      <v-app-bar-nav-icon color="primary" @click="drawer = !drawer">
        <v-icon icon="mdi-menu" />
      </v-app-bar-nav-icon>
    </template>
    <v-app-bar-title class="dashboad title ma-0 pa-0 mr-4">
      <router-link class="home w-100" to="/">SEEQ</router-link> {{ title }}
    </v-app-bar-title>

    <span class="text-caption text-disabled pa-2">v{{ version }}</span>
  </v-app-bar>
  <v-navigation-drawer
    v-if="me"
    v-model="drawer"
    :mini-variant="mini_variant"
    temporary
    hide-overlay
    bottom
    app
    :clipped="clipped">
    <v-list>
      <v-list-item @click="view_me">
        <template #prepend>
          <v-avatar v-if="me?.avatar?.length" rounded="lg" size="48">
            <v-img :src="me.avatar" alt="My Avatar" />
          </v-avatar>
          <v-avatar v-else color="primary" size="48">
            <span class="text-h6">{{ initials }}</span>
          </v-avatar>
        </template>
        <v-list-item-title class="text-h6">{{ me.name }}</v-list-item-title>
      </v-list-item>
      <v-divider class="my-2" />

      <v-list-item
        v-for="company in me.staff"
        :key="company.id"
        exact
        @click="view_company(company)">
        <template #prepend>
          <v-avatar v-if="company.logo" size="24" rounded="lg">
            <v-img :src="company.logo" :alt="`${company.name} logo`" />
          </v-avatar>
          <v-icon v-else>mdi-domain</v-icon>
        </template>
        <v-list-item-title>{{ company.name }}</v-list-item-title>
      </v-list-item>

      <v-divider v-if="me.staff?.length" class="my-2" />

      <v-list-item
        v-if="me.participating?.length"
        to="/participants"
        :router
        exact>
        <template #prepend>
          <v-icon>mdi-account-group-outline</v-icon>
        </template>
        <v-list-item-title>Participating</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="roles(['Sponsor', 'Researcher', 'Recruiter'])"
        to="/projects"
        :router
        exact>
        <template #prepend>
          <v-icon>mdi-view-dashboard</v-icon>
        </template>
        <v-list-item-title>Projects</v-list-item-title>
      </v-list-item>

      <v-list-item v-if="roles(['Master Admin'])" exact to="/companies">
        <template #prepend>
          <v-icon>mdi-domain</v-icon>
        </template>
        <v-list-item-title>Admin</v-list-item-title>
      </v-list-item>

      <v-divider class="my-2" />

      <v-list-item exact density="compact" @click="view_terms">
        <template #prepend>
          <v-icon size="small">mdi-scale-balance</v-icon>
        </template>
        <v-list-item-title class="text-caption">Terms of Use</v-list-item-title>
      </v-list-item>
      <v-list-item exact density="compact" @click="view_privacy">
        <template #prepend>
          <v-icon size="small">mdi-eye-off</v-icon>
        </template>
        <v-list-item-title class="text-caption"
          >Privacy Policy</v-list-item-title
        >
      </v-list-item>
      <v-list-item
        exact
        density="compact"
        @click="
          async () => {
            await signout()
            router.push('/')
          }
        ">
        <template #prepend>
          <v-icon size="small">mdi-logout</v-icon>
        </template>
        <v-list-item-title class="text-caption">Sign out</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
  <header></header>
  <v-main class="mb-8">
    <v-overlay
      v-if="working"
      :model-value="working"
      opacity="0.3"
      class="d-flex align-center justify-center">
      <v-progress-circular color="primary" indeterminate size="124" width="6" />
    </v-overlay>
    <router-view />
  </v-main>
  <aside><notify-me /></aside>
</template>

<style>
  div.title a.home {
    text-decoration: none !important;
  }
</style>
